/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-01-24
 */

import { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Alert,
  CircularProgress,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { Add, Cancel, Refresh } from '@mui/icons-material';

// Add local dependencies.
import {
  MyJoinRequestsProps,
  JoinRequestStatus,
} from '../../../types/joinRequest';
import {
  useGetMyJoinRequestsQuery,
  useCancelJoinRequestMutation,
} from '../../../features/studios/studiosApi';
import { getApiErrorMessage } from '../../../utils/errorHandling';
import RequestStatusBadge from './RequestStatusBadge';

/**
 * MyJoinRequests component displays a user's join request history
 * and allows them to manage their pending requests.
 */
const MyJoinRequests = ({
  userId: _userId,
  onCancel,
  onResubmit,
  onRequestToJoin,
}: MyJoinRequestsProps) => {
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [requestToCancel, setRequestToCancel] = useState<string | null>(null);
  const [cancelError, setCancelError] = useState('');

  const {
    data: requestsData,
    isLoading,
    error,
    refetch,
  } = useGetMyJoinRequestsQuery({
    pageSize: 50, // Show more requests for user's own view
  });

  const [cancelJoinRequest, { isLoading: isCancelling }] =
    useCancelJoinRequestMutation();

  const requests = requestsData?.results || [];

  const handleCancelRequest = async () => {
    if (!requestToCancel) return;

    setCancelError('');
    try {
      await cancelJoinRequest(requestToCancel).unwrap();
      onCancel?.(requestToCancel);
      refetch();
      setCancelDialogOpen(false);
      setRequestToCancel(null);
    } catch (err) {
      // e.g. the studio approved or rejected the request meanwhile
      setCancelError(
        getApiErrorMessage(
          err,
          'Failed to cancel the request. Please try again.'
        )
      );
    }
  };

  const openCancelDialog = (requestId: string) => {
    setCancelError('');
    setRequestToCancel(requestId);
    setCancelDialogOpen(true);
  };

  const closeCancelDialog = () => {
    setCancelDialogOpen(false);
    setRequestToCancel(null);
    setCancelError('');
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Typography variant="h6">My Join Requests</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={() => refetch()}
            disabled={isLoading}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => onRequestToJoin?.()}
          >
            Request to Join Studio
          </Button>
        </Box>
      </Box>

      {/* Error State */}
      {error && (
        <Alert severity="error">
          Failed to load your join requests. Please try again.
        </Alert>
      )}

      {/* Loading State */}
      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      )}

      {/* Results */}
      {!isLoading &&
        !error &&
        (requests.length > 0 ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {requests.map(request => (
              <Card key={request.id} variant="outlined">
                <CardContent>
                  {/* Studio and Status */}
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      mb: 2,
                    }}
                  >
                    <Box>
                      <Typography variant="h6" component="div">
                        {request.studio_name}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Requested Role: {request.requested_role || 'Member'}
                      </Typography>
                    </Box>
                    <RequestStatusBadge
                      status={request.status}
                      submittedAt={request.submitted_at}
                      reviewedAt={request.reviewed_at}
                      size="small"
                    />
                  </Box>

                  {/* Request Message */}
                  <Box sx={{ mb: 2 }}>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      gutterBottom
                    >
                      Your Message:
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        fontStyle: request.message ? 'normal' : 'italic',
                        color: request.message
                          ? 'text.primary'
                          : 'text.secondary',
                        pl: 1,
                        borderLeft: 2,
                        borderColor: 'divider',
                      }}
                    >
                      {request.message || 'No message provided'}
                    </Typography>
                  </Box>

                  {/* Timestamps */}
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 0.5,
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      Submitted:{' '}
                      {new Date(request.submitted_at).toLocaleDateString()} at{' '}
                      {new Date(request.submitted_at).toLocaleTimeString()}
                    </Typography>
                    {request.reviewed_at && (
                      <Typography variant="caption" color="text.secondary">
                        Reviewed:{' '}
                        {new Date(request.reviewed_at).toLocaleDateString()} at{' '}
                        {new Date(request.reviewed_at).toLocaleTimeString()}
                        {request.reviewed_by_name &&
                          ` by ${request.reviewed_by_name}`}
                      </Typography>
                    )}
                  </Box>

                  {/* Denial Reason */}
                  {request.status === JoinRequestStatus.REJECTED &&
                    request.denialReason && (
                      <Box
                        sx={{
                          mt: 2,
                          p: 1.5,
                          bgcolor: 'error.50',
                          borderRadius: 1,
                        }}
                      >
                        <Typography
                          variant="body2"
                          color="error.main"
                          fontWeight="medium"
                          gutterBottom
                        >
                          Reason for Denial:
                        </Typography>
                        <Typography variant="body2" color="error.main">
                          {request.denialReason}
                        </Typography>
                      </Box>
                    )}

                  {/* Actions */}
                  {request.status === JoinRequestStatus.PENDING && (
                    <>
                      {!request.user && (
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          component="p"
                          sx={{ mt: 2 }}
                        >
                          This studio can&apos;t approve this request yet. If
                          you recently changed your email address, confirm it
                          from the link we sent; otherwise please send a new
                          request.
                        </Typography>
                      )}
                      <Divider sx={{ my: 2 }} />
                      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Button
                          size="small"
                          color="error"
                          startIcon={<Cancel />}
                          onClick={() => openCancelDialog(request.id)}
                        >
                          Cancel Request
                        </Button>
                      </Box>
                    </>
                  )}

                  {/* Resubmit Option for Denied Requests */}
                  {request.status === JoinRequestStatus.REJECTED &&
                    request.studioId && (
                      <>
                        <Divider sx={{ my: 2 }} />
                        <Box
                          sx={{ display: 'flex', justifyContent: 'flex-end' }}
                        >
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => onResubmit?.(request.studioId!)}
                          >
                            Submit New Request
                          </Button>
                        </Box>
                      </>
                    )}
                </CardContent>
              </Card>
            ))}
          </Box>
        ) : (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No join requests found
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              You have not submitted any requests to join studios yet.
            </Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={() => onRequestToJoin?.()}
            >
              Request to Join a Studio
            </Button>
          </Box>
        ))}

      {/* Cancel Confirmation Dialog */}
      <Dialog
        open={cancelDialogOpen}
        onClose={closeCancelDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Cancel Join Request</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to cancel this join request? This action
            cannot be undone.
          </Typography>
          {cancelError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {cancelError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={closeCancelDialog} disabled={isCancelling}>
            Keep Request
          </Button>
          <Button
            onClick={handleCancelRequest}
            color="error"
            variant="contained"
            disabled={isCancelling}
            startIcon={
              isCancelling ? <CircularProgress size={16} /> : <Cancel />
            }
          >
            {isCancelling ? 'Cancelling...' : 'Cancel Request'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MyJoinRequests;
